import type { Ref } from 'vue'

import {
  compareParticipantByLineupOrder,
  compareParticipantByShortname,
} from '@collector/shared-ui-domain'
import {
  SubParticipantFlag,
  type SubParticipantPosition,
} from '@collector/sportsapi-client-legacy'
import type { SubParticipant } from '@collector/sportsapi-types'

import type { LineupModel, LineupPlayer } from '../interfaces/LineupModel'

export function cloneWithKey(
  lineup: Ref<LineupModel>,
  ...keys: PropertyKey[]
): LineupModel {
  const cloned = { ...lineup.value }
  keys.forEach((key) => (cloned[key] = [...(cloned[key] || [])]))
  return cloned
}

export function toSortedByLineupOrder<TParticipant extends SubParticipant>(
  subParticipants: TParticipant[],
): TParticipant[] {
  return [...subParticipants].sort(compareParticipantByLineupOrder)
}
export function toSortedBySurname<TParticipant extends SubParticipant>(
  subParticipants: TParticipant[],
): TParticipant[] {
  return [...subParticipants].sort(compareParticipantByShortname)
}

export function filterPosition<T extends SubParticipant>(
  players: T[],
  position: SubParticipantPosition,
): T[] {
  return players.filter((p) => p.position === position)
}

export function isPossibleBatter(player: SubParticipant): boolean {
  return player.flags?.includes(SubParticipantFlag.PossibleBatter) ?? false
}

export function hasSecondaryPosition(player: SubParticipant): boolean {
  return (
    ('has_secondary_position' in player && !!player.has_secondary_position) ||
    isPossibleBatter(player)
  )
}

export function withHasSecondaryPosition(
  player: SubParticipant,
  extra = false,
): LineupPlayer {
  return {
    ...player,
    has_secondary_position: extra || hasSecondaryPosition(player),
  }
}

export function filterHasSecondaryPosition<T extends SubParticipant>(
  players: T[],
): T[] {
  return players.filter(hasSecondaryPosition)
}

export function getTeamIdToSubParticipantsMap(
  subParticipants: SubParticipant[],
): Record<string, SubParticipant[]> {
  return subParticipants.reduce(
    (acc, subParticipant) => {
      if (subParticipant.team_id) {
        acc[subParticipant.team_id] = acc[subParticipant.team_id] || []
        acc[subParticipant.team_id].push(subParticipant)
      }
      return acc
    },
    {} as Record<string, SubParticipant[]>,
  )
}
